import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { 
  fetchLikedProductCount,
  fetchLikedProduct,
  fetchGuestLikedProduct,
  likedProductById, 
  removeLikeFromProduct, 
  fetchImagesByMaterialForHomePage,
  fetchMaterialIconsByProductIds,
  persistProductSelection
} from "../../services";
import {IProductLikedState  } from "../../components/interfaces";

const guestLikedStorageKey = 'truekarat_guest_liked_product_ids';

const readGuestLikedProductIds = (): string[] => {
  if (typeof window === 'undefined') return [];

  try {
    const storedIds = JSON.parse(window.localStorage.getItem(guestLikedStorageKey) || '[]');
    return Array.isArray(storedIds)
      ? Array.from(new Set(storedIds.filter((id): id is string => typeof id === 'string' && Boolean(id))))
      : [];
  } catch {
    return [];
  }
};

const persistGuestLikedProductIds = (ids: string[]) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(guestLikedStorageKey, JSON.stringify(ids));
};


const initialState: IProductLikedState = {
  likedProducts: [],
  likedProductCountAndIds: {
    count: 0,
    ids: [],
  },
  status: 'idle',
  error: '',
  productImagesByProductId: {},
  materialIconsByProductId: {},
  statusByProductId: {},
  errorByProductId: {},
};

export const likedProductSlice = createSlice({
  name: 'likedProducts',
  initialState,
  reducers: {
    setLikedItemsCount: (state, action: PayloadAction<number>) => {
      state.likedProductCountAndIds.count = action.payload;
    },
    clearLiked: (state) => {
      state.likedProducts = [];
      state.likedProductCountAndIds = {
        count: 0,
        ids: [],
      };
    },
    loadGuestLiked: (state) => {
      const ids = readGuestLikedProductIds();
      state.likedProducts = [];
      state.likedProductCountAndIds = { count: ids.length, ids };
      state.status = 'idle';
      state.error = '';
    },
    addGuestLike: (state, action: PayloadAction<string>) => {
      if (!state.likedProductCountAndIds.ids.includes(action.payload)) {
        state.likedProductCountAndIds.ids.push(action.payload);
        state.likedProductCountAndIds.count = state.likedProductCountAndIds.ids.length;
        persistGuestLikedProductIds(state.likedProductCountAndIds.ids);
      }
    },
    removeGuestLike: (state, action: PayloadAction<string>) => {
      state.likedProductCountAndIds.ids = state.likedProductCountAndIds.ids.filter(
        (id) => id !== action.payload
      );
      state.likedProductCountAndIds.count = state.likedProductCountAndIds.ids.length;
      state.likedProducts = state.likedProducts.filter((product) => product.id !== action.payload);
      persistGuestLikedProductIds(state.likedProductCountAndIds.ids);
    },
    updateSelectedMaterialInLiked: (
      state,
      action: PayloadAction<{ productId: string; materialName: string }>
    ) => {
      const { productId, materialName } = action.payload;
      const product = state.likedProducts.find((p) => p.id === productId);
      if (product) {
        product.selectedMaterial = materialName;
      }
    },
    addLikeLocal: (state, action: PayloadAction<string>) => {
      if (!state.likedProductCountAndIds.ids.includes(action.payload)) {
        state.likedProductCountAndIds.ids.push(action.payload);
        state.likedProductCountAndIds.count += 1;
      }
    },
    removeLikeLocal: (state, action: PayloadAction<string>) => {
      const index = state.likedProductCountAndIds.ids.indexOf(action.payload);
      if (index !== -1) {
        state.likedProductCountAndIds.ids.splice(index, 1);
        state.likedProductCountAndIds.count -= 1;
      }
      state.likedProducts = state.likedProducts.filter((product) => product.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLikedProduct.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(fetchLikedProduct.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.likedProducts = action.payload;
        state.likedProductCountAndIds = {
          count: action.payload.length,
          ids: action.payload.map((product) => product.id),
        };
      })
      .addCase(fetchLikedProduct.rejected, (state, action) => {
        state.status = 'failed';
        state.error =
          typeof action.payload === 'string'
            ? action.payload
          : action.error.message || 'An unknown error occurred';
      })
      .addCase(fetchGuestLikedProduct.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(fetchGuestLikedProduct.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.likedProducts = action.payload;
        state.likedProductCountAndIds = {
          count: action.payload.length,
          ids: action.payload.map((product) => product.id),
        };
        persistGuestLikedProductIds(state.likedProductCountAndIds.ids);
      })
      .addCase(fetchGuestLikedProduct.rejected, (state, action) => {
        state.status = 'failed';
        state.error =
          typeof action.payload === 'string'
            ? action.payload
            : action.error.message || 'An unknown error occurred';
      })
      .addCase(likedProductById.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(removeLikeFromProduct.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(fetchLikedProductCount.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(fetchLikedProductCount.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.likedProductCountAndIds = {
          count: action.payload.count,
          ids: action.payload.ids,
        };
      })
      .addCase(fetchLikedProductCount.rejected, (state, action) => {
        state.status = 'failed';
        state.error =
          typeof action.payload === 'string'
            ? action.payload
            : action.error.message || 'An unknown error occurred';
      })
      .addCase(fetchImagesByMaterialForHomePage.pending, (state, action) => {
        if (action.meta.arg.source === 'liked') {
          const productId = action.meta.arg.productId;
          state.statusByProductId[productId] = 'loading';
          state.errorByProductId[productId] = '';
        }
      })
      .addCase(fetchImagesByMaterialForHomePage.fulfilled, (state, action) => {
        if (action.payload.source === 'liked') {
          const { productId, images } = action.payload;
          state.productImagesByProductId[productId] = images.map(img => img.image.publicUrl);
          state.statusByProductId[productId] = 'succeeded';
        }
      })
      .addCase(fetchImagesByMaterialForHomePage.rejected, (state, action) => {
        if (action.meta.arg.source === 'liked') {
          const productId = action.meta.arg.productId;
          state.statusByProductId[productId] = 'failed';
          state.errorByProductId[productId] = action.payload || 'Unknown error';
        }
      })
      .addCase(fetchMaterialIconsByProductIds.fulfilled, (state, action) => {
        if ((action.meta.arg as any).source === 'liked') {
          state.materialIconsByProductId = {
            ...state.materialIconsByProductId,
            ...action.payload,
          };
        }
      })
      .addCase(persistProductSelection.fulfilled, (state, action) => {
        const product = state.likedProducts.find((item) => item.id === action.payload.productId);
        if (product && action.payload.selectedMaterial) {
          product.selectedMaterial = action.payload.selectedMaterial;
        }
      });

  }
});

export const {
  setLikedItemsCount,
  clearLiked,
  loadGuestLiked,
  addGuestLike,
  removeGuestLike,
  updateSelectedMaterialInLiked,
  removeLikeLocal,
  addLikeLocal,
} = likedProductSlice.actions;

export default likedProductSlice.reducer;
